import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DataserviceService } from '../dataservice.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CdkDropList, CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';

interface BlitzResult {
  contactedCount?: number;
  successCount?: number;
  notes?: string;
}

interface Blitz {
  startDate: Date;
  endDate: Date;
  repCount: number;
  results?: BlitzResult;
}

interface MarketHistory {
  date: Date;
  changeType: 'ASSIGNMENT' | 'BLITZ_ADDED' | 'BLITZ_COMPLETED' | 'LEAD_COUNT_UPDATED';
  description: string;
  changedBy: string;
}

interface MarketData {
  id: string;
  name: string;
  provider: string;
  leadCount: number;
  penetration: number;
  assignedUser?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  offer: string;
  competition: string;
  active: boolean;
  blitzes: Blitz[];
  history: MarketHistory[];
}

@Component({
  selector: 'app-request-areas',
  templateUrl: './request-areas.component.html',
  styleUrl: './request-areas.component.css',
})
export class RequestAreasComponent implements OnInit {
  @ViewChildren('marketCardList') marketDropLists!: QueryList<CdkDropList>;
  boardColumns: { id: string; title: string; markets: MarketData[] }[] = [];
  connectedLists: CdkDropList[] = [];
  areasForm: FormGroup;
  marketsList: MarketData[] = [];
  isLoading: boolean = false;
  loadingMessage: string = '';
  currentUser: any;
  users: any[] = [];
  pins: any;
  providers: any[] = [];
  markets: any[] = [];
  isMarketModalVisible = false;
  editingMarket: MarketData | null = null;
  marketForm: FormGroup;
  isBlitzModalVisible = false;
  editingBlitz: { marketId: string; blitzIndex: number; blitz: Blitz } | null = null;
  blitzForm: FormGroup;

  constructor(private fb: FormBuilder, private data: DataserviceService, private message: NzMessageService) {
    this.currentUser = JSON.parse(localStorage.getItem('user')!);

    this.areasForm = this.fb.group({
      market: ['', Validators.required],
      user: [null, Validators.required],
      count: ['', Validators.required],
      dateRange: [[null, null], Validators.required],
    });

    this.marketForm = this.fb.group({
      name: ['', [Validators.required]],
      provider: ['', Validators.required],
      leadCount: [0, [Validators.required, Validators.min(0)]],
      penetration: [0, [Validators.required, Validators.min(0)]],
      offer: ['', Validators.required],
      competition: ['', Validators.required],
      active: [false]
    });

    this.blitzForm = this.fb.group({
      dateRange: [null, Validators.required],
      startDate: [null, Validators.required],
      endDate: [null, Validators.required],
      repCount: [1, [Validators.required, Validators.min(1)]],
      results: this.fb.group({
        contactedCount: [0],
        successCount: [0],
        notes: ['']
      })
    });
  }

  ngOnInit() {
    this.getUsers();
    this.getMarketData();
    this.getProviders();
  }

  loadingHelper(onOff: boolean, message?: string) {
    this.loadingMessage = message || 'Loading...';
    this.isLoading = onOff;
  }

  getUsers() {
    this.loadingHelper(true, 'Loading users...');
    this.data.getData('users').subscribe(
      (response: any) => {
        this.users = response;
        this.loadingHelper(false);
      },
      (error) => {
        this.message.error('Failed to load users');
        this.loadingHelper(false);
      }
    );
  }


  getMarketData() {
    this.loadingHelper(true, 'Loading market data...');
    this.data.getData('market-mgmt').subscribe(
      (response: any) => {
        this.marketsList = Array.isArray(response) ? response : response?.data ?? [];
        this.buildBoardColumns();
        this.loadingHelper(false);
        setTimeout(() => {
          if (this.marketDropLists) this.connectedLists = this.marketDropLists.toArray();
        }, 0);
      },
      (error) => {
        this.message.error('Failed to load market data');
        this.loadingHelper(false);
      }
    );
  }

  buildBoardColumns() {
    const unassigned = this.marketsList.filter(
      (m: MarketData) => !m.assignedUser || m.assignedUser.id == null
    );
    const userColumns = (this.users || []).map((u: any) => ({
      id: String(u.id),
      title: `${u.first_name || ''} ${u.last_name || ''}`.trim() || `User ${u.id}`,
      markets: this.marketsList.filter((m: MarketData) => m.assignedUser?.id === u.id),
    }));
    this.boardColumns = [
      { id: 'unassigned', title: 'Unassigned', markets: unassigned },
      ...userColumns,
    ];
  }

  ngAfterViewChecked() {
    if (this.marketDropLists?.length && this.marketDropLists.length !== this.connectedLists.length) {
      setTimeout(() => {
        this.connectedLists = this.marketDropLists.toArray();
      }, 0);
    }
  }

  unassignMarket(market: MarketData) {
    this.data.updateData({ assignedUser: null }, `market-mgmt/${market.id}`).subscribe(
      () => {
        this.message.success('Market unassigned');
        this.getMarketData();
      },
      () => this.message.error('Failed to unassign market')
    );
  }

  onMarketDrop(event: CdkDragDrop<MarketData[]>) {
    if (event.previousContainer === event.container) return;
    const market = event.previousContainer.data[event.previousIndex];
    const targetId = event.container.id;
    transferArrayItem(
      event.previousContainer.data,
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
    if (targetId === 'unassigned') {
      this.unassignMarket(market);
    } else {
      const user = this.users.find((u: any) => String(u.id) === targetId);
      if (user) this.assignUserToMarket(market, user.id);
    }
  }

  assignUserToMarket(market: MarketData, userId: number) {
    const user = this.users.find(u => u.id === userId);
    if (user) {
      const updateData: any = {
        assignedUser: {
          id: user.id,
          firstName: user.first_name,
          lastName: user.last_name
        }
      };

      this.data.updateData(updateData, `market-mgmt/${market.id}`).subscribe(
        (response: MarketData) => {
          const index = this.marketsList.findIndex(m => m.id === market.id);
          if (index !== -1) {
            this.marketsList[index] = response;
          }
          this.message.success('User assigned successfully');
          this.getMarketData();
        },
        (error) => {
          this.message.error('Failed to assign user');
        }
      );
    }
  }

  updateMarketActive(market: MarketData, active: boolean) {
    const updateData: any = {
      active: active
    };
    this.data.updateData(updateData, `market-mgmt/${market.id}`).subscribe(
      (response: MarketData) => {
        this.message.success('Market active status updated successfully');
        this.getMarketData();
      },
      (error) => {
        this.message.error('Failed to update market active status');
      }
    );
  }


  addBlitz(market: MarketData) {
    this.showBlitzModal(market);
  }

  getProviders() {
    this.loadingHelper(true, 'Loading providers...');
    this.data.getData('providers').subscribe(
      (response: any) => {
        this.providers = response;
        this.loadingHelper(false);
      },
      (error) => {
        this.message.error('Failed to load providers');
        this.loadingHelper(false);
      }
    );
  }

  showMarketModal(market?: MarketData) {
    this.editingMarket = market || null;
    if (market) {
      this.marketForm.patchValue({
        name: market.name,
        provider: market.provider,
        leadCount: market.leadCount,
        penetration: market.penetration,
        offer: market.offer,
        competition: market.competition,
        active: market.active
      });
    } else {
      this.marketForm.reset({
        leadCount: 0,
        penetration: 0,
        active: false
      });
    }
    this.isMarketModalVisible = true;
  }

  handleMarketModalCancel() {
    this.isMarketModalVisible = false;
    this.marketForm.reset();
    this.editingMarket = null;
  }

  handleMarketModalOk() {
    if (this.marketForm.valid) {
      const marketData = this.marketForm.value;

      if (this.editingMarket) {
        // Update existing market
        this.data.updateData(marketData, `market-mgmt/${this.editingMarket.id}`).subscribe(
          (response: MarketData) => {
            const index = this.marketsList.findIndex(m => m.id === this.editingMarket!.id);
            if (index !== -1) {
              this.marketsList[index] = response;
            }
            this.message.success('Market updated successfully');
            this.getMarketData();
            this.handleMarketModalCancel();
          },
          (error) => {
            this.message.error('Failed to update market');
          }
        );
      } else {
        // Create new market
        this.data.postData(marketData, 'market-mgmt').subscribe(
          (response: MarketData) => {
            this.getMarketData();
            this.message.success('Market created successfully');
            this.handleMarketModalCancel();
          },
          (error) => {
            this.message.error('Failed to create market');
          }
        );
      }
    } else {
      Object.values(this.marketForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsTouched();
        }
      });
    }
  }

  showBlitzModal(market: any, blitzIndex?: number) {
    if (blitzIndex !== undefined) {
      // Editing existing blitz
      const blitz = market.blitzes[blitzIndex].body;
      const startDate = blitz.startDate ? new Date(blitz.startDate) : null;
      const endDate = blitz.endDate ? new Date(blitz.endDate) : null;

      this.editingBlitz = { marketId: market.id, blitzIndex, blitz };
      this.blitzForm.patchValue({
        dateRange: startDate && endDate ? [startDate, endDate] : null,
        startDate: startDate,
        endDate: endDate,
        repCount: blitz.repCount,
        results: blitz.results || { contactedCount: 0, successCount: 0, notes: '' },
      });
    } else {
      // Adding new blitz
      this.editingBlitz = { marketId: market.id, blitzIndex: -1, blitz: {} as Blitz };
      this.blitzForm.reset({
        dateRange: null,
        startDate: null,
        endDate: null,
        repCount: 1,
        results: { contactedCount: 0, successCount: 0, notes: '' }
      });
    }
    this.isBlitzModalVisible = true;
  }

  handleBlitzModalCancel() {
    this.isBlitzModalVisible = false;
    this.blitzForm.reset();
    this.editingBlitz = null;
  }

  handleBlitzModalOk() {
    if (this.blitzForm.valid && this.editingBlitz) {
      const formValue = this.blitzForm.value;

      // Ensure dates are properly formatted
      const blitzData = {
        startDate: formValue.startDate ? new Date(formValue.startDate) : null,
        endDate: formValue.endDate ? new Date(formValue.endDate) : null,
        repCount: formValue.repCount,
        results: formValue.results
      };

      // Validate dates
      if (!blitzData.startDate || !blitzData.endDate) {
        this.message.error('Please select valid dates');
        return;
      }

      if (this.editingBlitz.blitzIndex !== -1) {
        // Update existing blitz
        this.data.updateData(
          blitzData,
          `market-mgmt/${this.editingBlitz.marketId}/blitz/${this.editingBlitz.blitzIndex}`
        ).subscribe(
          (response: MarketData) => {
            const index = this.marketsList.findIndex(m => m.id === this.editingBlitz!.marketId);
            if (index !== -1) {
              this.marketsList[index] = response;
            }
            this.message.success('Blitz updated successfully');
            this.getMarketData();
            this.handleBlitzModalCancel();
          },
          (error) => {
            this.message.error('Failed to update blitz');
          }
        );
      } else {
        // Add new blitz
        this.data.postData(
          blitzData,
          `market-mgmt/${this.editingBlitz!.marketId}/blitz`
        ).subscribe(
          (response: MarketData) => {
            const index = this.marketsList.findIndex(m => m.id === this.editingBlitz!.marketId);
            if (index !== -1) {
              this.marketsList[index] = response;
            }
            this.message.success('Blitz added successfully');
            this.getMarketData();
            this.handleBlitzModalCancel();
          },
          (error) => {
            this.message.error('Failed to add blitz');
          }
        );
      }
    } else {
      Object.values(this.blitzForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsTouched();
        }
      });
    }
  }

  onDateRangeChange(dates: Date[]) {
    if (dates && dates.length === 2) {
      const [startDate, endDate] = dates;
      this.blitzForm.patchValue({
        startDate: startDate,
        endDate: endDate
      }, { emitEvent: false }); // Prevent infinite loop
    } else {
      this.blitzForm.patchValue({
        startDate: null,
        endDate: null
      }, { emitEvent: false });
    }
  }

  // Helper method to safely format dates for display
  formatDate(date: string | Date): string {
    if (!date) return '';
    const d = new Date(date);
    return d instanceof Date && !isNaN(d.getTime()) ? d.toLocaleDateString() : '';
  }
}
